/**
 * Erweitert BaseTable um Inline-Editing-Funktionalität
 */
import React, { useMemo, useCallback, useImperativeHandle, forwardRef, useRef, useEffect } from 'react';
import {
  Box,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Close as CloseIcon,
  Check as CheckIcon,
} from '@mui/icons-material';
import BaseTable, { TableColumn, TableData, BaseTableProps } from './BaseTable';
import EditableCell from './EditableCell';
import { useInlineEditing, NEW_ROW_ID } from './useInlineEditing';
import { EditableTableConfig, EditableFieldConfig } from './types';
import { colors } from '../../../styles/theme';

export interface EditableTableColumn extends TableColumn {
  /** Konfiguration für Inline-Editing (falls editierbar) */
  editConfig?: EditableFieldConfig;
}

export interface EditableTableProps<T extends TableData> extends Omit<BaseTableProps, 'columns' | 'data' | 'renderCell' | 'renderActions' | 'onRowClick' | 'renderExpandedRow'> {
  columns: EditableTableColumn[];
  data: T[];
  editConfig: EditableTableConfig<T>;
  /** Kann der Benutzer bearbeiten? */
  canEdit?: boolean;
  /** Custom Cell-Renderer für Anzeigemodus */
  renderCell?: (column: EditableTableColumn, row: T, value: any) => React.ReactNode;
  /** Callback nach Datenänderung (Create/Update/Delete) */
  onDataChanged?: () => void;
  /** Rendert expandierten Inhalt für eine Zeile (typisiert mit T) */
  renderExpandedRow?: (row: T) => React.ReactNode;
  /** Zusätzliche Aktions-Buttons für jede Zeile (werden vor dem Löschen-Button angezeigt) */
  customRowActions?: (row: T) => React.ReactNode;
  /** Callback wenn eine Zeile angeklickt wird (unabhängig vom Edit-Modus) */
  onRowSelect?: (row: T) => void;
  /** ID der aktuell selektierten Zeile (Highlight im Nicht-Edit-Modus) */
  selectedRowId?: string;
}

export interface EditableTableRef {
  /** Startet den Create-Modus (neue Zeile) mit optionalen initialen Werten */
  startCreating: (initialValues?: Record<string, any>) => void;
  cancelEditing: () => void;
}

function EditableTableInner<T extends TableData>(
  props: EditableTableProps<T>,
  ref: React.ForwardedRef<EditableTableRef>
) {
  const {
    columns,
    data,
    editConfig,
    canEdit = false,
    renderCell: customRenderCell,
    onDataChanged,
    renderExpandedRow,
    customRowActions,
    onRowSelect,
    selectedRowId: externalSelectedRowId,
    ...baseTableProps
  } = props;

  const containerRef = useRef<HTMLDivElement>(null);

  // Ref für den letzten Feldwert (um Race-Conditions beim Speichern zu vermeiden)
  const lastFieldUpdateRef = useRef<{ field: string; value: any } | null>(null);

  // Helper: Liest und leert den Ref, gibt fieldOverride zurück
  const consumeLastFieldUpdate = useCallback((): Partial<T> | undefined => {
    const pendingUpdate = lastFieldUpdateRef.current;
    lastFieldUpdateRef.current = null;
    return pendingUpdate
      ? { [pendingUpdate.field]: pendingUpdate.value } as Partial<T>
      : undefined;
  }, []);

  // Inline-Editing Hook (mit Original-Daten für korrekte Validierung)
  const {
    editingRowId,
    newRowData,
    pendingChanges,
    errors,
    isSaving,
    isCreating,
    startEditing,
    startCreating,
    updateField,
    saveRow,
    saveNewRow,
    cancelEditing,
    deleteRow,
  } = useInlineEditing(editConfig, data, onDataChanged);

  // Expose Methoden nach außen (für LoanSection)
  useImperativeHandle(ref, () => ({
    startCreating,
    cancelEditing,
  }), [startCreating, cancelEditing]);

  // Ref leeren wenn Create-Modus beendet wird
  useEffect(() => {
    if (!isCreating) {
      lastFieldUpdateRef.current = null;
    }
  }, [isCreating]);

  // Click-Outside: Bearbeitung beenden wenn außerhalb der Tabelle geklickt wird
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!editingRowId) return;

      const target = event.target as HTMLElement;

      if (containerRef.current?.contains(target)) {
        return; // Klick innerhalb - nichts tun
      }

      // Prüfe ob der Klick auf ein MUI-Popup/Dropdown/Menu war
      // Diese werden als Portale außerhalb des Containers gerendert
      const isClickOnMuiPopup = target.closest(
        '.MuiPopover-root, .MuiMenu-root, .MuiModal-root, .MuiPopper-root, .MuiAutocomplete-popper'
      );
      if (isClickOnMuiPopup) {
        return; // Klick auf Dropdown-Menü - nichts tun
      }

      if (editingRowId === NEW_ROW_ID) {
        saveNewRow(consumeLastFieldUpdate());
      } else {
        // Für existierende Zeilen: Pending Changes speichern und dann Edit beenden
        saveRow(editingRowId).then(() => {
          cancelEditing();
        });
      }
    };

    // Event-Listener hinzufügen (mit Verzögerung um aktuelle Klicks nicht zu erfassen)
    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 0);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [editingRowId, cancelEditing, saveRow, saveNewRow, consumeLastFieldUpdate]);

  // Kombiniere Daten mit neuer Zeile (falls im Create-Modus)
  const tableData = useMemo(() => {
    if (isCreating && newRowData !== null) {
      const newRow: T = {
        id: NEW_ROW_ID,
        ...newRowData,
      } as T;
      return [...data, newRow];
    }
    return data;
  }, [data, isCreating, newRowData]);

  // Aktuellen Wert für eine Zelle ermitteln (inkl. pending changes)
  const getCellValue = useCallback((row: T, columnKey: string): any => {
    const rowId = row.id;

    if (rowId === NEW_ROW_ID && newRowData) {
      return newRowData[columnKey as keyof T] ?? row[columnKey];
    }

    const pending = pendingChanges[rowId];
    if (pending && columnKey in pending) {
      return pending[columnKey as keyof T];
    }

    return row[columnKey];
  }, [newRowData, pendingChanges]);

  const handleFieldChange = useCallback((rowId: string, columnKey: string, value: any) => {
    // Speichere das letzte Update für den Fall, dass der Haken geklickt wird
    // bevor der State aktualisiert wurde (gilt für neue UND existierende Zeilen)
    lastFieldUpdateRef.current = { field: columnKey, value };
    updateField(rowId, columnKey as keyof T, value);
  }, [updateField]);

  const handleFieldBlur = useCallback((rowId: string) => {
    if (rowId !== NEW_ROW_ID) {
      // Auto-Save für existierende Zeilen (mit letztem Feld-Update als Override)
      saveRow(rowId, consumeLastFieldUpdate());
    }
    // Für neue Zeilen: Kein Auto-Save, erst beim expliziten Speichern
  }, [saveRow, consumeLastFieldUpdate]);

  // Handler für Enter-Taste (Speichern und Edit beenden)
  // Akzeptiert den aktuellen Wert direkt, um Race-Conditions zu vermeiden
  const handleEnterPress = useCallback((rowId: string, columnKey: string, currentValue: any) => {
    // Ref leeren, da der Wert direkt übergeben wird
    lastFieldUpdateRef.current = null;

    const fieldOverride = { [columnKey]: currentValue } as Partial<T>;

    if (rowId === NEW_ROW_ID) {
      saveNewRow(fieldOverride);
    } else {
      // Existierende Zeile speichern - mit dem aktuellen Wert als Override
      saveRow(rowId, fieldOverride).then(() => {
        cancelEditing();
      });
    }
  }, [saveNewRow, saveRow, cancelEditing]);

  const handleRowClick = useCallback((row: TableData) => {
    if (row.id === NEW_ROW_ID) return;

    // Der Klick auf eine andere Zeile beendet die laufende Bearbeitung, statt in ihr hängen zu
    // bleiben. Gespeichert wird dabei wie beim Klick außerhalb der Tabelle - der Wechsel darf
    // Eingaben nicht verschlucken. saveRow liest die Änderungen synchron, ein späteres
    // cancelEditing kann den laufenden Aufruf also nicht mehr leeren.
    const bearbeitetAndereZeile = editingRowId !== null && editingRowId !== row.id;
    if (bearbeitetAndereZeile && !isSaving) {
      if (editingRowId === NEW_ROW_ID) {
        saveNewRow(consumeLastFieldUpdate());
      } else {
        // Das Verlassen des Feldes hat in der Regel schon gespeichert; steht noch etwas aus,
        // holt saveRow es nach und tut sonst nichts.
        saveRow(editingRowId, consumeLastFieldUpdate());
      }
    }

    onRowSelect?.(row as T);

    if (!canEdit) return;

    // Tabellen mit Auswahl (onRowSelect) trennen die beiden Absichten: Der erste Klick wählt die
    // Zeile aus - die Auswahl steuert außerhalb der Tabelle etwas mit -, erst der Klick auf die
    // bereits ausgewählte Zeile öffnet die Bearbeitung. Ohne Auswahl bleibt es beim einen Klick,
    // sonst bräuchten die übrigen Tabellen plötzlich zwei.
    if (onRowSelect && externalSelectedRowId !== row.id) {
      if (bearbeitetAndereZeile && editingRowId !== NEW_ROW_ID) {
        cancelEditing();
      }
      return;
    }

    startEditing(row.id);
  }, [
    canEdit,
    startEditing,
    onRowSelect,
    externalSelectedRowId,
    editingRowId,
    isSaving,
    saveRow,
    saveNewRow,
    cancelEditing,
    consumeLastFieldUpdate,
  ]);

  const handleDelete = useCallback(async (rowId: string, e: React.MouseEvent) => {
    e.stopPropagation();

    if (rowId === NEW_ROW_ID) {
      cancelEditing();
      return;
    }

    if (window.confirm('Möchten Sie diesen Eintrag wirklich löschen?')) {
      await deleteRow(rowId);
    }
  }, [cancelEditing, deleteRow]);

  const handleSaveNewRow = useCallback(async (e: React.MouseEvent) => {
    e.stopPropagation();
    await saveNewRow(consumeLastFieldUpdate());
  }, [saveNewRow, consumeLastFieldUpdate]);

  // Cell-Renderer mit Edit-Support
  const renderCellWithEdit = useCallback((column: EditableTableColumn, row: T, value: any) => {
    const rowId = row.id;
    const isRowEditing = editingRowId === rowId;
    const columnKey = column.key;

    const currentValue = getCellValue(row, columnKey);

    const rowErrors = errors[rowId];
    const fieldError = rowErrors?.[columnKey];

    const editFieldConfig = column.editConfig;

    // Wenn getOptions definiert ist, dynamische Options basierend auf der Zeile generieren
    const resolvedFieldConfig = editFieldConfig ? {
      ...editFieldConfig,
      options: editFieldConfig.getOptions
        ? editFieldConfig.getOptions(row)
        : editFieldConfig.options,
    } : undefined;

    if (isRowEditing && resolvedFieldConfig && canEdit) {
      return (
        <EditableCell
          value={currentValue}
          fieldConfig={resolvedFieldConfig}
          isEditing={true}
          error={fieldError}
          onChange={(newValue) => handleFieldChange(rowId, columnKey, newValue)}
          onBlur={() => handleFieldBlur(rowId)}
          onEnterPress={(currentValue) => handleEnterPress(rowId, columnKey, currentValue)}
          renderDisplay={customRenderCell ? (v) => customRenderCell(column, row, v) : undefined}
        />
      );
    }

    if (customRenderCell) {
      const customResult = customRenderCell(column, row, currentValue);
      if (customResult !== undefined) {
        return customResult;
      }
    }

    // Standard-Anzeige über EditableCell (nutzt dessen Display-Logic)
    if (resolvedFieldConfig) {
      return (
        <EditableCell
          value={currentValue}
          fieldConfig={resolvedFieldConfig}
          isEditing={false}
          onChange={() => { }}
          onBlur={() => { }}
        />
      );
    }

    return value;
  }, [editingRowId, canEdit, getCellValue, errors, handleFieldChange, handleFieldBlur, handleEnterPress, customRenderCell]);

  // Actions-Renderer (nur für Create-Modus-Buttons und Custom-Actions)
  const renderActions = useCallback((row: TableData) => {
    const rowId = row.id;
    const isNewRow = rowId === NEW_ROW_ID;

    if (isNewRow) {
      return (
        <>
          <Tooltip title="Speichern">
            <span>
              <IconButton
                size="small"
                onClick={handleSaveNewRow}
                disabled={isSaving}
                sx={{
                  color: colors.tableActions.save.main,
                  '&:hover': {
                    backgroundColor: colors.tableActions.save.hover,
                  },
                }}
              >
                <CheckIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Abbrechen">
            <span>
              <IconButton
                size="small"
                onClick={(e) => handleDelete(rowId, e)}
                disabled={isSaving}
                sx={{
                  color: colors.tableActions.cancel.main,
                  '&:hover': {
                    backgroundColor: colors.tableActions.cancel.hover,
                  },
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </>
      );
    }

    if (!customRowActions) return null;
    return customRowActions(row as T);
  }, [isSaving, handleSaveNewRow, handleDelete, customRowActions]);

  const handleDeleteRow = useCallback((row: TableData, e: React.MouseEvent) => {
    handleDelete(row.id, e);
  }, [handleDelete]);

  const generalError = errors[editingRowId || '']?._general;

  return (
    <Box ref={containerRef} sx={{ position: 'relative' }}>
      {/* Saving Indicator */}
      {isSaving && (
        <Box
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            zIndex: 10,
          }}
        >
          <CircularProgress size={20} />
        </Box>
      )}

      {/* Genereller Fehler */}
      {generalError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {generalError}
        </Alert>
      )}

      {/* Tabelle */}
      <BaseTable
        {...baseTableProps}
        columns={columns}
        data={tableData}
        renderCell={(column, row, value) => renderCellWithEdit(column as EditableTableColumn, row as T, value)}
        renderActions={canEdit ? renderActions : undefined}
        onDelete={canEdit ? handleDeleteRow : undefined}
        onRowClick={(canEdit || onRowSelect) ? handleRowClick : undefined}
        selectedRowId={editingRowId || externalSelectedRowId || undefined}
        renderExpandedRow={renderExpandedRow as ((row: TableData) => React.ReactNode) | undefined}
      />
    </Box>
  );
}

// ForwardRef mit Generics
const EditableTable = forwardRef(EditableTableInner) as <T extends TableData>(
  props: EditableTableProps<T> & { ref?: React.ForwardedRef<EditableTableRef> }
) => React.ReactElement;

export default EditableTable;
